import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { formatError } from "../lib/api";
import { Loader2, ShieldCheck, Globe } from "lucide-react";

export default function Login() {
  const { login, l1, ready } = useAuth();
  const { t, lang, toggle } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ready && l1 === false) nav("/emergent-admin-login", { replace: true }); }, [ready, l1, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (e) { setErr(formatError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between qatar-gradient p-12 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.pexels.com/photos/38941832/pexels-photo-38941832.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "overlay" }} />
        <div className="relative">
          <div className="mb-8 flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/15 text-2xl font-bold font-head">Q</div>
            <p className="text-sm uppercase tracking-widest text-white/70">{t("company")}</p>
          </div>
          <h1 className="max-w-md text-4xl font-bold font-head leading-tight">Enterprise AI Governance Operating System</h1>
          <p className="mt-4 max-w-sm text-sm text-white/80">{t("subtitle")}</p>
        </div>
        <div className="relative flex items-center gap-2 text-sm text-white/80"><ShieldCheck className="h-5 w-5" /> Layer 1 verified — sign in to continue.</div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2"><div className="qatar-gradient grid h-10 w-10 place-items-center rounded-xl text-white font-bold font-head">Q</div></div>
            <button data-testid="language-switcher-login" onClick={toggle} className="ms-auto inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"><Globe className="h-4 w-4" /> {lang === "en" ? "العربية" : "English"}</button>
          </div>
          <div className="mb-1 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 w-fit"><ShieldCheck className="h-3.5 w-3.5" /> Administrator identity verified</div>
          <h2 className="mt-3 text-2xl font-bold font-head text-slate-900">Enterprise AI Governance Operating System</h2>
          <p className="mt-1 text-sm text-slate-500">Sign in with your application credentials.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("email")}</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--qatar)] focus:ring-2 focus:ring-[var(--qatar-border)]" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("password")}</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--qatar)] focus:ring-2 focus:ring-[var(--qatar-border)]" />
            </div>
            {err && <p data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="qatar-gradient flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("signing_in")}</> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
